// Phase v1.3 (MUI-05 / D-COACH-*) — coach-hint client hook.
//
// Single-call state machine wrapping `POST /api/match/{matchId}/coach-hint`.
// Consumer is `MatchScreen` (Plan Task 6); the hook does NOT render anything
// itself — it returns `{ state, request, dismiss }`.
//
// State transitions:
//   idle ──request()──▶ loading ──fetch ok──▶ findable_word
//                                           │
//                                           ├─▶ powerup_timing
//                                           │
//                                           ├─▶ cooldown
//                                           │
//                                           └─▶ none
//                              │
//                              └──fetch error──▶ error
//
// dismiss() → idle (callable from any phase).
// Auto-reset on `turnIdx` change is the CONSUMER's responsibility — the
// hook accepts no turnIdx prop; MatchScreen drives the reset via dismiss()
// in a useEffect on current_turn_idx (Plan Task 6).
//
// re-entry: while phase === 'loading', request() is a no-op (prevents
// double-fire on a rapid double-tap).

import { useCallback, useState } from 'react'

import { ApiError, apiFetch } from '../../net/client'

export type PowerupTimingRule =
  | 'bank_full'
  | 'empower_soon'
  | 'hold_for_reveal'
  | 'consider_velvet'

export type CoachHintState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'loading' }
  | { readonly phase: 'findable_word'; readonly cells: readonly number[] }
  | {
      readonly phase: 'powerup_timing'
      readonly rule: PowerupTimingRule
      readonly messageKey: string
    }
  | { readonly phase: 'cooldown' }
  | { readonly phase: 'none' }
  | { readonly phase: 'error'; readonly message: string }

export interface UseCoachHintReturn {
  readonly state: CoachHintState
  readonly request: () => Promise<void>
  readonly dismiss: () => void
}

interface CoachHintResponseBody {
  kind: 'findable_word' | 'powerup_timing' | 'cooldown' | 'none'
  payload?:
    | { cells: number[] }
    | { rule: PowerupTimingRule; messageKey: string }
}

export function useCoachHint(matchId: string): UseCoachHintReturn {
  const [state, setState] = useState<CoachHintState>({ phase: 'idle' })

  const request = useCallback(async () => {
    // Guard re-entry while a request is in-flight.
    if (state.phase === 'loading') return
    setState({ phase: 'loading' })
    try {
      const body = await apiFetch<CoachHintResponseBody>(
        `/api/match/${matchId}/coach-hint`,
        'POST',
        { body: {} },
      )
      switch (body.kind) {
        case 'findable_word': {
          const payload = (body.payload ?? {}) as { cells?: readonly number[] }
          setState({
            phase: 'findable_word',
            cells: payload.cells ?? [],
          })
          break
        }
        case 'powerup_timing': {
          const payload = (body.payload ?? {}) as {
            rule?: PowerupTimingRule
            messageKey?: string
          }
          setState({
            phase: 'powerup_timing',
            rule: payload.rule ?? 'empower_soon',
            messageKey: payload.messageKey ?? '',
          })
          break
        }
        case 'cooldown':
          setState({ phase: 'cooldown' })
          break
        case 'none':
          setState({ phase: 'none' })
          break
        default:
          setState({ phase: 'error', message: 'unexpected hint kind' })
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `coach-hint request failed (${err.status})`
          : 'coach-hint request failed'
      setState({ phase: 'error', message })
    }
  }, [matchId, state.phase])

  const dismiss = useCallback(() => {
    setState({ phase: 'idle' })
  }, [])

  return { state, request, dismiss }
}
